import React, { type JSX } from 'react';

const BTN =
  'rounded-md border border-gray-300 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

type Props = {
  rowCount: number;
  disabled?: boolean;
  onExpand: () => void;
  onCopyCsv: () => void | Promise<void>;
  onDownloadCsv: () => void;
};

/** Expand / copy / export controls for verification tables in transition dialogs. */
export default function VerificationTableActions(props: Props): JSX.Element {
  const { rowCount, disabled = false, onExpand, onCopyCsv, onDownloadCsv } = props;
  const noRows = rowCount === 0;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button type="button" className={BTN} disabled={disabled || noRows} onClick={onExpand}>
        Expand
      </button>
      <button
        type="button"
        className={BTN}
        disabled={disabled || noRows}
        onClick={() => void onCopyCsv()}
      >
        Copy CSV
      </button>
      <button type="button" className={BTN} disabled={disabled || noRows} onClick={onDownloadCsv}>
        Export CSV
      </button>
    </div>
  );
}

export async function copyVerificationCsvToClipboard(csvText: string, toastLabel: string): Promise<void> {
  try {
    if (!navigator?.clipboard?.writeText) throw new Error('Clipboard unavailable');
    await navigator.clipboard.writeText(csvText);
    window.dispatchEvent(new CustomEvent('toast-info', { detail: `${toastLabel} copied` }));
  } catch {
    window.dispatchEvent(new CustomEvent('toast-error', { detail: `Unable to copy ${toastLabel}` }));
  }
}

export function downloadVerificationCsv(csvText: string, filenameBase: string): void {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const safe = String(filenameBase || 'verification')
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '_');
  const link = document.createElement('a');
  link.href = url;
  link.download = `${safe}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
